import { soundService } from '../services/soundService';
import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate, useLocation, useSearchParams, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Headphones, Play, Pause, RotateCcw, Volume2, Send, Lightbulb,
  CheckCircle2, ArrowLeft, ArrowRight, ChevronRight, Sparkles, RefreshCw, Globe2,
  Check, X, AlertCircle, ArrowUpRight, BarChart3, Undo2, Square
} from 'lucide-react';
import { AppBackButton, goBackOr, canGoBack } from '../components/AppBackButton';
import { useAuth } from '../contexts/AuthContext';
import { textToSpeechService } from '../services/textToSpeechService';
import { activeStudyTracker } from '../services/activeStudyTrackerService';
import { dbManager } from '../services/db_manager';
import { statsService } from '../services/statsService';
import { exerciseProgressService } from '../services/exerciseProgressService';
import { useUnifiedDictionary } from '../hooks/useUnifiedDictionary';
import { getLanguageCode } from '../services/languageConstants';
import { loadListeningChecklist, getListeningAudioUrls } from '../services/listeningAudioService';
import { StudyPageHeader } from '../components/StudyPageHeader';
import { AnimatedIcon } from '../components/AnimatedIcon';
import T from '../components/T';
import i18nInstance from '../i18n';

// --- HELPER: Split sentence into hint blocks ---
// Rules: '?' and '!' are separated into standalone blocks.
// All other punctuation remains attached to the preceding word.
export function splitSentenceIntoHintBlocks(sentence: string): string[] {
  if (!sentence) return [];
  const tokens: string[] = [];
  const rawWords = sentence.trim().split(/\s+/);

  for (const word of rawWords) {
    let current = "";
    for (let i = 0; i < word.length; i++) {
      const char = word[i];
      if (char === '?' || char === '!') {
        if (current.length > 0) {
          tokens.push(current);
          current = "";
        }
        tokens.push(char);
      } else {
        current += char;
      }
    }
    if (current.length > 0) {
      tokens.push(current);
    }
  }

  return tokens;
}

export function shuffleArray<T>(array: T[]): T[] {
  const arr = [...array];
  for (let i = arr.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [arr[i], arr[j]] = [arr[j], arr[i]];
  }
  return arr;
}

export function removeAccents(str: string): string {
  return (str || '').normalize("NFD").replace(/[\u0300-\u036f]/g, "");
}

export function computeCharDiff(target: string, typed: string | null): Array<{ char: string; isCorrect: boolean }> {
  if (!target) return [];
  if (!typed) {
    return target.split('').map(char => ({ char, isCorrect: false }));
  }

  if (target.toLowerCase() === typed.toLowerCase()) {
    return target.split('').map(char => ({ char, isCorrect: true }));
  }

  const m = target.length;
  const n = typed.length;
  const dp: number[][] = Array.from({ length: m + 1 }, () => Array(n + 1).fill(0));

  for (let i = 1; i <= m; i++) {
    for (let j = 1; j <= n; j++) {
      if (target[i - 1].toLowerCase() === typed[j - 1].toLowerCase()) {
        dp[i][j] = dp[i - 1][j - 1] + 1;
      } else {
        dp[i][j] = Math.max(dp[i - 1][j], dp[i][j - 1]);
      }
    }
  }

  const matchedTargetIndices = new Set<number>();
  let i = m;
  let j = n;
  while (i > 0 && j > 0) {
    if (target[i - 1].toLowerCase() === typed[j - 1].toLowerCase()) {
      matchedTargetIndices.add(i - 1);
      i--;
      j--;
    } else if (dp[i - 1][j] >= dp[i][j - 1]) {
      i--;
    } else {
      j--;
    }
  }

  return target.split('').map((char, idx) => {
    if (/\s/.test(char)) {
      return { char, isCorrect: true };
    }
    if (/[.,!?;:¡¿"']/.test(char) && !matchedTargetIndices.has(idx)) {
      return { char, isCorrect: true };
    }
    return {
      char,
      isCorrect: matchedTargetIndices.has(idx)
    };
  });
}

export function getLevenshteinDistance(a: string, b: string): number {
  if (a.length === 0) return b.length;
  if (b.length === 0) return a.length;
  const matrix: number[][] = [];
  for (let i = 0; i <= b.length; i++) {
    matrix[i] = [i];
  }
  for (let j = 0; j <= a.length; j++) {
    matrix[0][j] = j;
  }
  for (let i = 1; i <= b.length; i++) {
    for (let j = 1; j <= a.length; j++) {
      if (b.charAt(i - 1) === a.charAt(j - 1)) {
        matrix[i][j] = matrix[i - 1][j - 1];
      } else {
        matrix[i][j] = Math.min(
          matrix[i - 1][j - 1] + 1,
          Math.min(matrix[i][j - 1] + 1, matrix[i - 1][j] + 1)
        );
      }
    }
  }
  return matrix[b.length][a.length];
}

// --- HELPER: Evaluate text similarity (0 - 10) ---
export function evaluateTextComparison(userTyped: string, targetText: string): { score: number; matchedCount: number; totalWords: number } {
  if (!targetText) return { score: 10, matchedCount: 0, totalWords: 0 };

  const normUser = (userTyped || '').replace(/[’‘`]/g, "'").trim().toLowerCase();
  const normTarget = (targetText || '').replace(/[’‘`]/g, "'").trim().toLowerCase();

  const cleanUserPunct = normUser.replace(/[^\w\sÀ-ÿ']/g, '').replace(/\s+/g, ' ');
  const cleanTargetPunct = normTarget.replace(/[^\w\sÀ-ÿ']/g, '').replace(/\s+/g, ' ');

  if (cleanUserPunct === cleanTargetPunct) {
    const totalWords = cleanTargetPunct.split(' ').filter(Boolean).length;
    return { score: 10, matchedCount: totalWords, totalWords };
  }

  if (removeAccents(cleanUserPunct) === removeAccents(cleanTargetPunct)) {
    const totalWords = cleanTargetPunct.split(' ').filter(Boolean).length;
    return { score: 9.5, matchedCount: totalWords, totalWords };
  }

  const targetTokens = targetText.split(/(\s+)/);
  const userWordsClean = normUser
    .split(/\s+/)
    .map(w => w.replace(/^[^\wÀ-ÿ']+|[^\wÀ-ÿ']+$/g, ""))
    .filter(Boolean);

  interface TargetWordInfo {
    withApostrophe: string;
    stripped: string;
    unaccented: string;
  }
  const targetWordsInfo: TargetWordInfo[] = [];
  targetTokens.forEach(token => {
    if (/[a-zA-ZÀ-ÿ\w]/.test(token)) {
      const normToken = token.replace(/[’‘`]/g, "'").toLowerCase();
      const withApostrophe = normToken.replace(/^[^\wÀ-ÿ']+|[^\wÀ-ÿ']+$/g, "");
      const stripped = withApostrophe.replace(/'/g, "");
      targetWordsInfo.push({
        withApostrophe,
        stripped,
        unaccented: removeAccents(withApostrophe)
      });
    }
  });

  const M = targetWordsInfo.length;
  const N = userWordsClean.length;
  if (M === 0) return { score: 10, matchedCount: 0, totalWords: 0 };

  // Helper to score similarity for individual word match (0.0 to 1.0)
  const scoreWordSimilarity = (uClean: string, tInfo: TargetWordInfo): number => {
    if (!uClean || !tInfo.withApostrophe) return 0;
    if (uClean === tInfo.withApostrophe || uClean === tInfo.stripped) return 1.0;

    const uUnacc = removeAccents(uClean);
    if (uUnacc === tInfo.unaccented) return 0.95;

    const dExact = getLevenshteinDistance(uClean, tInfo.withApostrophe);
    const dUnacc = getLevenshteinDistance(uUnacc, tInfo.unaccented);
    const minD = Math.min(dExact, dUnacc);

    // 1 edit away (1 extra letter, 1 missing letter, or 1 slight typo, e.g. "enssemble" vs "ensemble", "canone" vs "canon")
    if (dExact === 1) {
      return 0.85;
    }
    // 1 edit away with accent difference (e.g. "rivier" vs "rivière")
    if (dUnacc === 1) {
      return 0.80;
    }

    // 2 edits away
    if (dExact === 2 && tInfo.withApostrophe.length >= 5) {
      return 0.70;
    }
    if (dUnacc === 2 && tInfo.unaccented.length >= 5) {
      return 0.65;
    }

    // Proportional character similarity for other cases
    const maxLen = Math.max(uClean.length, tInfo.withApostrophe.length);
    if (maxLen > 0) {
      const sim = (maxLen - minD) / maxLen;
      if (sim >= 0.7) return Math.round(sim * 10) / 10;
      if (sim >= 0.5) return Math.round(sim * 8) / 10;
      if (sim >= 0.3) return 0.3;
    }
    return 0;
  };

  const dp: number[][] = Array.from({ length: M + 1 }, () => Array(N + 1).fill(0));
  for (let i = 1; i <= M; i++) {
    for (let j = 1; j <= N; j++) {
      dp[i][j] = Math.max(dp[i - 1][j], dp[i][j - 1]);
      const tCurr = targetWordsInfo[i - 1];
      const uCurrClean = userWordsClean[j - 1];

      const wordVal = scoreWordSimilarity(uCurrClean, tCurr);
      if (wordVal > 0) {
        dp[i][j] = Math.max(dp[i][j], dp[i - 1][j - 1] + wordVal);
      }
    }
  }

  const matchedScore = dp[M][N];
  let calculatedScore = Math.round((matchedScore / M) * 10 * 10) / 10;
  if (calculatedScore > 10) calculatedScore = 10;
  return { score: calculatedScore, matchedCount: Math.round(matchedScore), totalWords: M };
}

const R2_LANG_NAMES: Record<string, string> = {
  'en': 'Inglês',
  'es': 'Espanhol',
  'fr': 'Francês',
  'de': 'Alemão',
  'it': 'Italiano',
  'pt': 'Português',
  'nl': 'Holandês',
  'ja': 'Japonês',
  'ru': 'Russo',
  'zh': 'Chinês',
  'hu': 'Húngaro'
};

const LANGUAGES = [
  { id: 'en', code: 'us', name: 'Inglês', r2Name: 'english', speechLocale: 'en-US', flag: '🇺🇸' },
  { id: 'es', code: 'es', name: 'Espanhol', r2Name: 'spanish', speechLocale: 'es-ES', flag: '🇪🇸' },
  { id: 'fr', code: 'fr', name: 'Francês', r2Name: 'french', speechLocale: 'fr-FR', flag: '🇫🇷' },
  { id: 'de', code: 'de', name: 'Alemão', r2Name: 'german', speechLocale: 'de-DE', flag: '🇩🇪' },
  { id: 'it', code: 'it', name: 'Italiano', r2Name: 'italian', speechLocale: 'it-IT', flag: '🇮🇹' },
  { id: 'hu', code: 'hu', name: 'Húngaro', r2Name: 'hungarian', speechLocale: 'hu-HU', flag: '🇭🇺' },
  { id: 'ru', code: 'ru', name: 'Russo', r2Name: 'russian', speechLocale: 'ru-RU', flag: '🇷🇺' },
  { id: 'pt', code: 'br', name: 'Português', r2Name: 'portuguese', speechLocale: 'pt-BR', flag: '🇧🇷' },
];

const LEVELS = [
  { id: 'A1', label: 'A1 - Iniciante' },
  { id: 'A2', label: 'A2 - Básico' },
  { id: 'B1', label: 'B1 - Intermediário' },
  { id: 'B2', label: 'B2 - Independente' },
  { id: 'C1', label: 'C1 - Avançado' },
  { id: 'C2', label: 'C2 - Domínio' }
];

const SPEEDS = [0.75, 0.5, 1.0];

export const ListeningPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { langCode: routeLang, level: routeLevel } = useParams<{ langCode?: string; level?: string }>();
  const location = useLocation();
  const isGuide = location.pathname.includes('/guide') || searchParams.get('from') === 'guide';
  const paramLang = routeLang ? getLanguageCode(routeLang) : searchParams.get('lang');
  const paramLevel = routeLevel || searchParams.get('level');

  const { t } = useTranslation();
  const { user } = useAuth();
  const unifiedDict = useUnifiedDictionary();

  // Selection states
  const [selectedLang, setSelectedLang] = useState<string>(() => paramLang ? getLanguageCode(paramLang) : 'en');
  const [selectedLevel, setSelectedLevel] = useState<string>(() => (paramLevel ? paramLevel.toUpperCase() : 'A1'));

  useEffect(() => {
    if (routeLang) {
      setSelectedLang(getLanguageCode(routeLang));
    } else if (searchParams.get('lang')) {
      setSelectedLang(getLanguageCode(searchParams.get('lang')!));
    }
    if (routeLevel) {
      setSelectedLevel(routeLevel.toUpperCase());
    } else if (searchParams.get('level')) {
      setSelectedLevel(searchParams.get('level')!.toUpperCase());
    }
  }, [routeLang, routeLevel, searchParams]);
  const [contentType, setContentType] = useState<'palavras' | 'frases'>('palavras');
  const [speedIndex, setSpeedIndex] = useState<number>(0); // Default 0.75x
  const currentSpeed = SPEEDS[speedIndex];

  // Cards & loading states
  const [cards, setCards] = useState<any[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [currentIndex, setCurrentIndex] = useState<number>(0);
  const [historyStack, setHistoryStack] = useState<number[]>([]);
  const [completedSessionCount, setCompletedSessionCount] = useState<number>(0);

  // Card interaction states
  const [hintActive, setHintActive] = useState<boolean>(false);
  const [userInput, setUserInput] = useState<string>('');
  const [submittedInput, setSubmittedInput] = useState<string | null>(null);
  const [evalScore, setEvalScore] = useState<number | null>(null);

  // Hint mode states
  const [hintBlocks, setHintBlocks] = useState<Array<{ id: string; text: string }>>([]);
  const [selectedBlocks, setSelectedBlocks] = useState<Array<{ id: string; text: string }>>([]);
  const [shakeBlockId, setShakeBlockId] = useState<string | null>(null);
  const [hintCompleted, setHintCompleted] = useState<boolean>(false);

  // Audio playing states
  const [isPlaying, setIsPlaying] = useState<boolean>(false);
  const [audioProgress, setAudioProgress] = useState<number>(0);
  const audioIntervalRef = useRef<any>(null);
  const audioRef = useRef<HTMLAudioElement | null>(null);

  // Preload listening checklist JSON in background when in 'frases' mode
  useEffect(() => {
    if (contentType === 'frases') {
      loadListeningChecklist();
    }
  }, [contentType]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      stopAudio();
    };
  }, []);

  const activeLangObj = useMemo(() => {
    return LANGUAGES.find(l => l.id === selectedLang) || LANGUAGES[0];
  }, [selectedLang]);

  // Telemetry session tracking
  useEffect(() => {
    if (user?.id) {
      activeStudyTracker.setUserContext(user.id, selectedLang);
      activeStudyTracker.onRouteChange('/listening', user.id, selectedLang);
    }
    return () => {
      activeStudyTracker.flush();
    };
  }, [user?.id, selectedLang]);

  // Fetch cards from R2 Cloudflare (Palavras vs Frases)
  useEffect(() => {
    let isMounted = true;
    const loadCards = async () => {
      setLoading(true);
      try {
        let formatted: any[] = [];

        if (contentType === 'palavras') {
          const WORD_LANG_FOLDERS: Record<string, string> = {
            'en': 'Ingles', 'english': 'Ingles',
            'pt': 'Portugues', 'portuguese': 'Portugues',
            'fr': 'Frances', 'french': 'Frances',
            'es': 'Espanhol', 'spanish': 'Espanhol',
            'de': 'Alemao', 'german': 'Alemao',
            'it': 'Italiano', 'italian': 'Italiano',
            'ru': 'Russo', 'russian': 'Russo',
            'hu': 'Hungaro', 'hungarian': 'Hungaro'
          };
          const WORD_SHORT_CODES: Record<string, string> = {
            'en': 'en', 'english': 'en',
            'pt': 'pt', 'portuguese': 'pt',
            'fr': 'fr', 'french': 'fr',
            'es': 'es', 'spanish': 'es',
            'de': 'de', 'german': 'de',
            'it': 'it', 'italian': 'it',
            'ru': 'ru', 'russian': 'ru',
            'hu': 'hu', 'hungarian': 'hu'
          };
          const targetFolder = WORD_LANG_FOLDERS[selectedLang] || 'Ingles';
          const srcCode = WORD_SHORT_CODES[selectedLang] || 'en';
          const tgtCode = 'pt';
          const lvl = selectedLevel.toUpperCase();

          const url1 = `https://pub-3d7ad7f5d04d46a5b7232c0baf46d866.r2.dev/new-flashcards/${targetFolder}/${lvl}/${srcCode}-${tgtCode}.json?t=${Date.now()}`;
          const url2 = `https://pub-3d7ad7f5d04d46a5b7232c0baf46d866.r2.dev/biblioteca-backup/new-flashcards/${targetFolder}/${lvl}/${srcCode}-${tgtCode}.json?t=${Date.now()}`;

          let res = await fetch(url1).catch(() => null);
          if (!res || !res.ok) {
            res = await fetch(url2).catch(() => null);
          }

          if (res && res.ok) {
            const json = await res.json();
            let rawList: any[] = [];

            if (Array.isArray(json)) {
              rawList = json;
            } else if (typeof json === 'object') {
              if (json[selectedLevel] && Array.isArray(json[selectedLevel])) {
                rawList = json[selectedLevel];
              } else if (json.decks && Array.isArray(json.decks)) {
                rawList = json.decks;
              } else {
                Object.keys(json).forEach(k => {
                  if (Array.isArray(json[k])) rawList.push(...json[k]);
                });
              }
            }

            formatted = rawList.map((item, idx) => ({
              id: item.id || `palavra_${selectedLang}_${selectedLevel}_${idx}`,
              front: item.word || item.front || item.phrase || item.original || '',
              back: Array.isArray(item.translations) ? item.translations.join(', ') : (item.translation || item.back || item.verso || ''),
              level: item.level || selectedLevel
            })).filter(c => !!c.front);
          }
        } else {
          // FRASES: Fetch from flashcards-store
          const targetLangName = R2_LANG_NAMES[selectedLang] || 'Inglês';
          const nativeLangName = 'Português';

          const targetUrl1 = `https://pub-3d7ad7f5d04d46a5b7232c0baf46d866.r2.dev/flashcards-store/${selectedLevel}/${encodeURIComponent(targetLangName)} - ${selectedLevel}.json?t=${Date.now()}`;
          const nativeUrl1 = `https://pub-3d7ad7f5d04d46a5b7232c0baf46d866.r2.dev/flashcards-store/${selectedLevel}/${encodeURIComponent(nativeLangName)} - ${selectedLevel}.json?t=${Date.now()}`;

          const targetUrl2 = `https://pub-3d7ad7f5d04d46a5b7232c0baf46d866.r2.dev/biblioteca-backup/flashcards-store/${selectedLevel}/${encodeURIComponent(targetLangName)} - ${selectedLevel}.json?t=${Date.now()}`;
          const nativeUrl2 = `https://pub-3d7ad7f5d04d46a5b7232c0baf46d866.r2.dev/biblioteca-backup/flashcards-store/${selectedLevel}/${encodeURIComponent(nativeLangName)} - ${selectedLevel}.json?t=${Date.now()}`;

          let [targetRes, nativeRes] = await Promise.all([
            fetch(targetUrl1).catch(() => null),
            fetch(nativeUrl1).catch(() => null)
          ]);

          if (!targetRes || !targetRes.ok || !nativeRes || !nativeRes.ok) {
            [targetRes, nativeRes] = await Promise.all([
              fetch(targetUrl2).catch(() => null),
              fetch(nativeUrl2).catch(() => null)
            ]);
          }

          if (targetRes && targetRes.ok) {
            const targetData = await targetRes.json();
            const nativeData = (nativeRes && nativeRes.ok) ? await nativeRes.json() : [];

            if (Array.isArray(targetData)) {
              formatted = targetData.map((item: any, idx: number) => {
                const nativeItem = Array.isArray(nativeData) ? nativeData[idx] || {} : {};
                return {
                  id: item.id || `frase_${selectedLang}_${selectedLevel}_${idx}`,
                  front: item.text || item.front || item.phrase || item.original || item.word || '',
                  back: nativeItem.text || nativeItem.translation || item.back || item.translation || '',
                  level: selectedLevel
                };
              }).filter(c => !!c.front);
            }
          }
        }

        if (isMounted) {
          const shuffled = formatted.length > 0 ? shuffleArray(formatted) : [];
          setCards(shuffled);
          setCurrentIndex(0);
          setHistoryStack([]);
          setCompletedSessionCount(0);
        }
      } catch (err) {
        console.error('[ListeningPage] Failed to fetch cards:', err);
        if (isMounted) setCards([]);
      } finally {
        if (isMounted) setLoading(false);
      }
    };

    loadCards();
    return () => { isMounted = false; };
  }, [selectedLang, selectedLevel, contentType, activeLangObj]);

  const currentCard = cards[currentIndex] || null;

  // Initialize hint blocks when current card or hint mode changes
  useEffect(() => {
    if (currentCard && hintActive) {
      const rawTokens = splitSentenceIntoHintBlocks(currentCard.front);
      const blocksWithIds = rawTokens.map((tok, i) => ({
        id: `blk_${i}_${tok}`,
        text: tok
      }));
      setHintBlocks(shuffleArray(blocksWithIds));
      setSelectedBlocks([]);
      setHintCompleted(false);
    } else {
      setHintBlocks([]);
      setSelectedBlocks([]);
      setHintCompleted(false);
    }
    setUserInput('');
    setSubmittedInput(null);
    setEvalScore(null);
  }, [currentCard, hintActive]);

  // Audio control
  const stopAudio = () => {
    if (audioRef.current) {
      try {
        audioRef.current.pause();
        audioRef.current.currentTime = 0;
        audioRef.current.ontimeupdate = null;
        audioRef.current.onended = null;
        audioRef.current.onerror = null;
      } catch (e) {}
      audioRef.current = null;
    }
    try { textToSpeechService.stop(); } catch (e) {}
    if (audioIntervalRef.current) {
      clearInterval(audioIntervalRef.current);
      audioIntervalRef.current = null;
    }
    setIsPlaying(false);
    setAudioProgress(0);
  };

  const playAudio = async () => {
    if (!currentCard || !currentCard.front) return;
    stopAudio();

    setIsPlaying(true);
    setAudioProgress(0);

    const locale = activeLangObj.speechLocale;

    // Se o exercício for de 'FRASES', buscar áudio pré-gravado no Cloudflare R2
    if (contentType === 'frases') {
      const audioUrls = getListeningAudioUrls(
        activeLangObj.r2Name || selectedLang,
        selectedLevel,
        currentCard.id,
        currentCard.front
      );

      if (audioUrls) {
        const tryPlayAudioUrl = (url: string): Promise<boolean> => {
          return new Promise((resolve) => {
            try {
              const audio = new Audio(url);
              audio.playbackRate = currentSpeed;
              audioRef.current = audio;

              audio.ontimeupdate = () => {
                if (audio.duration && audio.duration > 0) {
                  const pct = Math.min(100, Math.round((audio.currentTime / audio.duration) * 100));
                  setAudioProgress(pct);
                }
              };

              audio.onended = () => {
                stopAudio();
              };

              audio.onerror = () => {
                audioRef.current = null;
                resolve(false);
              };

              audio.play().then(() => {
                resolve(true);
              }).catch(() => {
                audioRef.current = null;
                resolve(false);
              });
            } catch {
              resolve(false);
            }
          });
        };

        // 1. Tentar URL primária do R2
        let success = await tryPlayAudioUrl(audioUrls.primaryUrl);
        // 2. Se falhar, tentar URL com fallback biblioteca-backup/
        if (!success && audioUrls.fallbackUrl) {
          success = await tryPlayAudioUrl(audioUrls.fallbackUrl);
        }

        if (success) {
          return; // Áudio pré-gravado do R2 reproduzido com sucesso!
        }
      }
    }

    // Fallback padrão para TTS (caso seja 'palavras' ou pre-gravado dê erro/não exista)
    textToSpeechService.speak(currentCard.front, locale, currentSpeed);

    // Approximate duration timer ring
    const estimatedDurationMs = Math.max(1500, (currentCard.front.length * 80) / currentSpeed);
    const startTime = Date.now();

    audioIntervalRef.current = setInterval(() => {
      const elapsed = Date.now() - startTime;
      const pct = Math.min(100, Math.round((elapsed / estimatedDurationMs) * 100));
      setAudioProgress(pct);

      if (pct >= 100) {
        clearInterval(audioIntervalRef.current);
        audioIntervalRef.current = null;
        setIsPlaying(false);
      }
    }, 50);
  };

  // Record exercise attempt, review timing & errors to D1 Cloudflare listening_exercise_erros table
  const recordExerciseAttempt = async (score: number, details: string, textInput?: string) => {
    if (!currentCard) return;
    const isPassed = score >= 6;

    let reviewIntervalMinutes = 10;
    if (score === 10) {
      reviewIntervalMinutes = 5760; // 4 days
    } else if (score >= 6) {
      reviewIntervalMinutes = 1440; // 1 day
    } else {
      reviewIntervalMinutes = 10; // 10 minutes
    }

    const nextReviewAt = new Date(Date.now() + reviewIntervalMinutes * 60 * 1000).toISOString();
    const nowIso = new Date().toISOString();

    // 1. D1 table recording (listening_exercise_erros) ONLY FOR SCORES <= 5
    if (!isPassed && user && user.id !== 'guest') {
      try {
        await dbManager.execute({
          action: 'upsert',
          table: 'listening_exercise_erros',
          user_id: user.id,
          data: {
            id: `${user.id}_${selectedLang}_${currentCard.id}`,
            user_id: user.id,
            language: selectedLang,
            error_date: nowIso,
            item_id: String(currentCard.id),
            score: score,
            review_interval_minutes: reviewIntervalMinutes,
            next_review_at: nextReviewAt,
            last_reviewed_at: nowIso,
            error_details: details || `Score: ${score}`,
            created_at: nowIso
          }
        });
      } catch (e) {
        console.warn('[ListeningPage] Error saving listening error to D1:', e);
      }
    }

    // 2. Telemetry and Reports (/reports) integration
    activeStudyTracker.logExercisePrecision(isPassed, user?.id, selectedLang);
    activeStudyTracker.logConsistencySession(user?.id, selectedLang);
    if (user && user.id !== 'guest') {
      statsService.increment('listening_count' as any, user.id, 1, { langCode: selectedLang });

      // Record unique words learned in listening to logs_unique_words table
      import('../services/uniqueWordsService').then(({ uniqueWordsService }) => {
        const cardText = currentCard.front || (currentCard as any).word || (currentCard as any).phrase || (currentCard as any).original || '';
        uniqueWordsService.recordExerciseCompletion(user.id, selectedLang, 'listening', currentCard.id, cardText);
      }).catch(err => console.warn('[ListeningPage] Error recording unique words:', err));

      // Record exercise progress for study plan & tracking
      exerciseProgressService.recordPractice({
        userId: user.id,
        exerciseType: 'listening',
        langCode: selectedLang,
        level: selectedLevel,
        itemId: String(currentCard.id),
        score: Math.round(score * 10),
        isSuccess: isPassed
      }).catch(err => console.warn('[ListeningPage] Error recording practice:', err));
    }

    // 3. Caderno de Erros (/corrections) integration for score <= 5
    if (!isPassed && user && user.id !== 'guest') {
      import('../services/errorReviewService').then(({ errorReviewService }) => {
        errorReviewService.recordError({
          userId: user.id,
          language: selectedLang,
          exerciseType: 'listening',
          sourceOrigin: 'direct_url',
          itemId: String(currentCard.id),
          itemPayload: {
            front: currentCard.front,
            back: currentCard.back,
            targetText: currentCard.front,
            translation: currentCard.back,
            original: currentCard.front
          },
          userInput: textInput || ''
        });
      });
    }
  };

  // Handle Text Submission (Dica OFF)
  const handleSubmitInput = () => {
    if (!currentCard || !userInput.trim()) return;
    const text = userInput.trim();
    setSubmittedInput(text);

    const { score } = evaluateTextComparison(text, currentCard.front);
    setEvalScore(score);
    soundService.play(score >= 7 ? 'success' : score >= 5 ? 'partial' : 'error');

    recordExerciseAttempt(score, `Text submission score: ${score} - input: ${text}`, text);

    if (score >= 6) {
      setCompletedSessionCount(prev => prev + 1);
    }
  };

  // Handle Hint Block Click (Dica ON)
  const handleBlockClick = (block: { id: string; text: string }) => {
    if (hintCompleted || !currentCard) return;

    const targetTokens = splitSentenceIntoHintBlocks(currentCard.front);
    const expectedToken = targetTokens[selectedBlocks.length];

    if (block.text === expectedToken) {
      // Correct block clicked
      const nextSelected = [...selectedBlocks, block];
      setSelectedBlocks(nextSelected);
      setHintBlocks(prev => prev.filter(b => b.id !== block.id));

      if (nextSelected.length === targetTokens.length) {
        setHintCompleted(true);
        setEvalScore(10);
        soundService.play('success');
        setCompletedSessionCount(prev => prev + 1);
        recordExerciseAttempt(10, `Completed hint blocks for card ${currentCard.id}`);
      }
    } else {
      // Incorrect block clicked
      setShakeBlockId(block.id);
      recordExerciseAttempt(4, `Clicked wrong block '${block.text}', expected '${expectedToken}'`);
      setTimeout(() => {
        setShakeBlockId(null);
      }, 1000);
    }
  };

  // Navigation handlers
  const handleNextCard = () => {
    stopAudio();
    setHintActive(false);
    setUserInput('');
    setSubmittedInput(null);
    setEvalScore(null);
    setSelectedBlocks([]);
    setHintCompleted(false);
    if (currentIndex < cards.length - 1) {
      setHistoryStack(prev => [...prev, currentIndex]);
      setCurrentIndex(prev => prev + 1);
    } else {
      // Loop or restart deck
      setHistoryStack(prev => [...prev, currentIndex]);
      setCurrentIndex(0);
    }
  };

  const handlePrevCard = () => {
    stopAudio();
    setHintActive(false);
    setUserInput('');
    setSubmittedInput(null);
    setEvalScore(null);
    setSelectedBlocks([]);
    setHintCompleted(false);
    if (historyStack.length > 0) {
      const prevIdx = historyStack[historyStack.length - 1];
      setHistoryStack(prev => prev.slice(0, -1));
      setCurrentIndex(prevIdx);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-950 text-ink p-4 sm:p-8 space-y-8 max-w-5xl mx-auto pb-24">
      {/* Top Header */}
      <div className="space-y-5 pb-2">
        <AppBackButton onClick={() => isGuide ? navigate('/') : goBackOr(navigate, '/study-hub')} />
        <StudyPageHeader tourKey="listening" title="Listening" />
      </div>

      {/* Selectors Bar (Idioma, Dificuldade & Tipo) - Listas suspensas idênticas ao /writing */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {/* Language Selector List */}
        <div data-tour="listening-language" className="bg-surface border border-black/[0.06] dark:border-white/[0.06] rounded-2xl p-4 relative">
          <label className="text-[10px] font-normal uppercase font-mono tracking-[0.14em] text-slate-400 mb-2 block flex items-center gap-2">
            <Globe2 size={14} className="text-primary" />
            Selecione o Idioma
          </label>
          <div className="relative">
            <select
              value={selectedLang}
              onChange={(e) => { setSelectedLang(e.target.value); stopAudio(); }}
              className="w-full appearance-none bg-surface outline-none pr-10 font-bold text-ink text-sm"
            >
              {LANGUAGES.map(l => (
                <option key={l.id} value={l.id} className="bg-surface text-ink">
                  {l.flag} {l.name}
                </option>
              ))}
            </select>
            <ChevronRight size={16} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none rotate-90" />
          </div>
        </div>

        {/* Level / Difficulty Selector List */}
        <div data-tour="listening-level" className="bg-surface border border-black/[0.06] dark:border-white/[0.06] rounded-2xl p-4 relative">
          <label className="text-[10px] font-normal uppercase font-mono tracking-[0.14em] text-slate-400 mb-2 block flex items-center gap-2">
            <BarChart3 size={14} className="text-primary" />
            Selecione a Dificuldade
          </label>
          <div className="relative">
            <select
              value={selectedLevel}
              onChange={(e) => { setSelectedLevel(e.target.value); stopAudio(); }}
              className="w-full appearance-none bg-surface outline-none pr-10 font-bold text-ink text-sm"
            >
              {LEVELS.map(lvl => (
                <option key={lvl.id} value={lvl.id} className="bg-surface text-ink">
                  {lvl.label}
                </option>
              ))}
            </select>
            <ChevronRight size={16} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none rotate-90" />
          </div>
        </div>

        {/* Content Type Selector List (Palavras vs Frases) */}
        <div data-tour="listening-content" className="bg-surface border border-black/[0.06] dark:border-white/[0.06] rounded-2xl p-4 relative">
          <label className="text-[10px] font-normal uppercase font-mono tracking-[0.14em] text-slate-400 mb-2 block flex items-center gap-2">
            <Sparkles size={14} className="text-primary" />
            Tipo de Conteúdo
          </label>
          <div className="relative">
            <select
              value={contentType}
              onChange={(e) => { setContentType(e.target.value as 'palavras' | 'frases'); stopAudio(); }}
              className="w-full appearance-none bg-surface outline-none pr-10 font-bold text-ink text-sm"
            >
              <option value="palavras" className="bg-surface text-ink">🔤 Palavras</option>
              <option value="frases" className="bg-surface text-ink">💬 Frases</option>
            </select>
            <ChevronRight size={16} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none rotate-90" />
          </div>
        </div>
      </div>

      {/* Main Content Area */}
      {loading ? (
        <div className="p-16 text-center rounded-3xl bg-surface border border-black/[0.06] dark:border-white/[0.06] space-y-4">
          <RefreshCw className="animate-spin text-primary mx-auto" size={32} />
          <p className="text-sm font-bold text-slate-400">Carregando exercícios de listening ({contentType})...</p>
        </div>
      ) : cards.length === 0 ? (
        <div className="p-16 text-center rounded-3xl bg-surface border border-black/[0.06] dark:border-white/[0.06] space-y-4">
          <AlertCircle className="text-warn mx-auto" size={40} />
          <h3 className="text-lg font-bold text-ink"><T k="ui.common.noExercises" /></h3>
          <p className="text-xs text-slate-400 max-w-md mx-auto">
            Não foram encontrados cards de {contentType} para {activeLangObj.name} no nível {selectedLevel}. Tente selecionar outro nível ou idioma.
          </p>
        </div>
      ) : (
        <div className="space-y-6">
          {/* Card Component */}
          <div className="relative rounded-3xl bg-surface border border-black/[0.06] dark:border-white/[0.06] p-6 sm:p-10 space-y-8 overflow-hidden">
            
            {/* Top Card Bar: Speed & Hint Toggles */}
            <div className="flex items-center justify-between">
              {/* Card Counter */}
              <span className="text-xs font-normal uppercase font-mono tracking-[0.14em] text-slate-400">
                Card {currentIndex + 1} de {cards.length} ({contentType === 'frases' ? 'Frases' : 'Palavras'})
              </span>

              <div className="flex items-center gap-3">
                {/* Speed Button */}
                <button data-tour="listening-speed"
                  onClick={() => {
                    setSpeedIndex((prev) => (prev + 1) % SPEEDS.length);
                  }}
                  className="h-9 px-3.5 rounded-full bg-tint-sky text-ink text-xs font-medium flex items-center gap-1.5 hover:opacity-85 transition-opacity tabular-nums"
                  title={i18nInstance.t('ui.listen.speed')}
                >
                  <AnimatedIcon motion="pulse"><Volume2 size={14} strokeWidth={1.75} /></AnimatedIcon>
                  <span>{currentSpeed}x</span>
                </button>

                {/* Hint Button */}
                <button data-tour="listening-hint"
                  onClick={() => setHintActive(!hintActive)}
                  className={`h-9 px-3.5 rounded-full text-xs font-medium transition-colors flex items-center gap-1.5 ${hintActive
                    ? 'bg-tint-butter text-ink'
                    : 'bg-transparent border border-ink/15 text-muted hover:text-ink'
                  }`}
                  title={hintActive ? 'Desativar modo Dica (blocos)' : 'Ativar modo Dica (blocos)'}
                >
                  <AnimatedIcon motion="swing" active={hintActive}><Lightbulb size={14} strokeWidth={1.75} /></AnimatedIcon>
                  <span>Dica</span>
                </button>
              </div>
            </div>

            {/* Central Play Button with Circular Timer Ring & Audio Equalizer Animation */}
            <div className="flex flex-col items-center justify-center py-6 space-y-4">
              <div className="relative flex items-center justify-center">
                {/* SVG Progress Ring */}
                <svg className="w-28 h-28 transform -rotate-90 pointer-events-none">
                  <circle
                    cx="56"
                    cy="56"
                    r="48"
                    className="stroke-ink/10"
                    strokeWidth="3"
                    fill="transparent"
                  />
                  <circle
                    cx="56"
                    cy="56"
                    r="48"
                    className="stroke-primary transition-all duration-75"
                    strokeWidth="3"
                    strokeDasharray={301.59}
                    strokeDashoffset={301.59 - (301.59 * audioProgress) / 100}
                    strokeLinecap="round"
                    fill="transparent"
                  />
                </svg>

                {/* Play / Stop Central Button */}
                <button data-tour="listening-play"
                  onClick={() => {
                    if (isPlaying) {
                      stopAudio();
                    } else {
                      playAudio();
                    }
                  }}
                  className={`absolute w-20 h-20 rounded-full flex items-center justify-center transition-all duration-300 active:scale-95 ${isPlaying
                    ? 'bg-ink text-surface scale-105'
                    : 'bg-tint-peach text-ink hover:scale-105'
                  }`}
                  title={isPlaying ? "Parar Áudio" : "Reproduzir Áudio"}
                >
                  {isPlaying ? <Square size={22} fill="currentColor" /> : <Play size={28} strokeWidth={1.6} className="ml-1" />}
                </button>
              </div>

              {/* Sound Wave Animation */}
              {isPlaying && (
                <div className="flex items-center gap-1 h-6">
                  <div className="w-1 bg-bad rounded-full animate-[bounce_1s_infinite_100ms] h-full" />
                  <div className="w-1 bg-bad rounded-full animate-[bounce_1s_infinite_300ms] h-3/4" />
                  <div className="w-1 bg-bad rounded-full animate-[bounce_1s_infinite_200ms] h-full" />
                  <div className="w-1 bg-bad rounded-full animate-[bounce_1s_infinite_400ms] h-1/2" />
                  <div className="w-1 bg-bad rounded-full animate-[bounce_1s_infinite_150ms] h-3/4" />
                </div>
              )}
            </div>

            {/* Exercise Mode Section */}
            {!hintActive ? (
              /* MODO 1: Digitação (Dica OFF) */
              <div data-tour="listening-mode" className="space-y-4 max-w-lg mx-auto">
                <div className="relative flex items-center">
                  <input
                    type="text"
                    value={userInput}
                    onChange={(e) => setUserInput(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter') handleSubmitInput(); }}
                    placeholder={contentType === 'frases' ? "Digite a frase que ouviu..." : "Digite a palavra que ouviu..."}
                    disabled={submittedInput !== null}
                    className="w-full h-14 px-6 rounded-full bg-tint-stone border border-transparent text-[15px] text-ink placeholder:text-muted focus:outline-none focus:border-ink/20 transition-all pr-16"
                  />
                  <button
                    onClick={handleSubmitInput}
                    disabled={!userInput.trim() || submittedInput !== null}
                    className="absolute right-2 w-10 h-10 rounded-full flex items-center justify-center bg-primary text-slate-900 disabled:opacity-25 disabled:cursor-not-allowed hover:opacity-90 transition-opacity"
                    title="Enviar resposta"
                  >
                    <Send size={16} />
                  </button>
                </div>

                {/* Submitted Evaluation Result */}
                {submittedInput !== null && evalScore !== null && (
                  <div className="p-5 rounded-[24px] bg-tint-stone space-y-3 animate-in fade-in duration-300">
                    <div className="text-center space-y-1">
                      <span className="text-[11px] font-normal uppercase font-mono tracking-[0.14em] text-slate-400">
                        {contentType === 'frases' ? 'Frase Original' : 'Palavra Original'}
                      </span>
                      <p className="text-base font-bold tracking-wide">
                        {computeCharDiff(currentCard.front, submittedInput).map((item, idx) => (
                          <span
                            key={idx}
                            className={item.isCorrect ? 'text-ok' : 'text-bad font-semibold underline decoration-bad/60'}
                          >
                            {item.char}
                          </span>
                        ))}
                      </p>
                      {currentCard.back && (
                        <p className="text-xs text-slate-400 italic">"{currentCard.back}"</p>
                      )}
                    </div>

                    {/* Discrete Score Message */}
                    <div className="text-center pt-2 border-t border-slate-800/80 space-y-1">
                      <span className={`text-xs font-normal uppercase font-mono tracking-[0.14em] ${evalScore >= 8 ? 'text-ok' : evalScore >= 6 ? 'text-ok' : 'text-bad'}`}>
                        Pontuação: {evalScore} / 10
                      </span>
                      {evalScore >= 6 && evalScore < 10 && (
                        <p className="text-[10px] text-warn font-medium">
                          {evalScore >= 9.5
                            ? 'Acento incorreto/faltante (-0.5 pt). Considerado correto!'
                            : 'Pequena divergência ortográfica/digitação. Considerado correto!'}
                        </p>
                      )}
                      {evalScore < 6 && (
                        <p className="text-[10px] text-bad font-medium">
                          Pontuação abaixo da média. Exercício reagendado para revisão.
                        </p>
                      )}
                      <p className="text-[10px] text-slate-400 font-normal uppercase font-mono tracking-[0.14em]">
                        Revisão agendada para daqui a {evalScore === 10 ? '4 dias' : evalScore >= 6 ? '1 dia' : '10 minutos'}
                      </p>
                    </div>
                  </div>
                )}
              </div>
            ) : (
              /* MODO 2: Formação por Blocos (Dica ON) */
              <div className="space-y-6 max-w-2xl mx-auto">
                {/* Target Sentence Area (As blocks are correctly clicked) */}
                <div className="p-5 rounded-[24px] bg-tint-stone min-h-[70px] flex flex-wrap items-center justify-center gap-2">
                  {selectedBlocks.length === 0 ? (
                    <span className="text-xs font-bold text-slate-500 italic">
                      Clique nos blocos abaixo na ordem correta...
                    </span>
                  ) : (
                    selectedBlocks.map((blk) => (
                      <span
                        key={blk.id}
                        className="px-3 py-1.5 rounded-xl bg-ok/20 border border-ok/40 text-ok font-bold text-sm animate-in zoom-in-90"
                      >
                        {blk.text}
                      </span>
                    ))
                  )}
                </div>

                {/* Available Shuffled Blocks */}
                {!hintCompleted ? (
                  <div className="flex flex-wrap items-center justify-center gap-2.5">
                    {hintBlocks.map((blk) => {
                      const isShaking = shakeBlockId === blk.id;
                      return (
                        <button
                          key={blk.id}
                          onClick={() => handleBlockClick(blk)}
                          className={`px-4 py-2.5 rounded-xl font-bold text-sm transition-all border active:scale-95 ${isShaking
                            ? 'bg-bad/30 border-bad text-bad animate-[bounce_0.3s_infinite]'
                            : 'bg-slate-100 dark:bg-slate-800 border-black/[0.1] dark:border-white/[0.1] text-slate-200 hover:bg-slate-700 hover:text-white'
                          }`}
                        >
                          {blk.text}
                        </button>
                      );
                    })}
                  </div>
                ) : (
                  <div className="p-4 rounded-2xl bg-ok/10 border border-ok/30 text-center space-y-2 animate-in fade-in duration-300">
                    <div className="flex items-center justify-center gap-2 text-ok font-semibold text-sm">
                      <CheckCircle2 size={20} />
                      <span><T k="ui.listen.done" /></span>
                    </div>
                    {currentCard.back && (
                      <p className="text-xs text-slate-400 italic">"{currentCard.back}"</p>
                    )}
                    <span className="text-xs font-normal uppercase font-mono tracking-[0.14em] text-ok block pt-1">
                      Pontuação: 10 / 10
                    </span>
                  </div>
                )}
              </div>
            )}

            {/* Bottom Card Navigation Bar */}
            <div className="flex items-center justify-between pt-6 border-t border-slate-800/80">
              {/* Voltar button (seta/flecha curvada para esquerda, cinza, sem texto) */}
              {historyStack.length > 0 || completedSessionCount > 0 ? (
                <button
                  onClick={handlePrevCard}
                  aria-label="Voltar ao card anterior"
                  className="p-3.5 rounded-2xl bg-slate-100 dark:bg-slate-800 border border-black/[0.1] dark:border-white/[0.1] text-slate-300 hover:text-white hover:bg-slate-700 transition-all active:scale-95 cursor-pointer flex items-center justify-center"
                >
                  <Undo2 size={20} />
                </button>
              ) : (
                <div />
              )}

              {/* Avançar / Próximo Card (flecha para a direita com traçado e ponta, botão verde, desabilitado até responder) */}
              {(() => {
                const isCardAnswered = evalScore !== null || hintCompleted;
                return (
                  <button
                    onClick={handleNextCard}
                    disabled={!isCardAnswered}
                    aria-label={i18nInstance.t('ui.listen.nextCard')}
                    className={`w-12 h-12 rounded-full transition-all flex items-center justify-center ${
                      !isCardAnswered
                        ? 'opacity-30 cursor-not-allowed border border-ink/15 text-muted'
                        : 'bg-ink text-surface active:scale-95 cursor-pointer hover:translate-x-0.5'
                    }`}
                  >
                    <ArrowRight size={18} strokeWidth={1.75} />
                  </button>
                );
              })()}
            </div>

          </div>
        </div>
      )}
    </div>
  );
};

export default ListeningPage;
